import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { getAllMasterFilterData } from '../equipment-list/LoadEquipmentService';
import { MY_LAB, ALL_EQUIPMENT } from '../../constants';

/**
 * Shared filter context for BookingGrid and EquipmentBookingGrid
 * Prevents state desynchronization when switching between tabs
 */
const SharedFilterContext = createContext();

export const useSharedFilterState = () => {
    const context = useContext(SharedFilterContext);
    if (!context) {
        throw new Error('useSharedFilterState must be used within a SharedFilterProvider');
    }
    return context;
};

export const SharedFilterProvider = ({ children, client, user }) => {
    // Centralized filter state
    const [filterState, setFilterState] = useState({
        masterDataFilters: {},
        myLabFilter: null,
        userFilters: [],
        currentFilter: null,
        isLoading: false,
        lastUpdate: null,
        error: null,
    });

    // Track initialization and loading to prevent unnecessary loads
    const initializationRef = useRef({
        isInitialized: false,
        isLoading: false,
        abortController: null,
    });

    // Cleanup function
    const cleanup = useCallback(() => {
        if (initializationRef.current.abortController) {
            initializationRef.current.abortController.abort();
            initializationRef.current.abortController = null;
        }
    }, []);

    // Memoized filter data loading function with proper cleanup
    const loadFilterData = useCallback(async (force = false) => {
        if (!user?.site || !user?.id) {
            return;
        }

        if (initializationRef.current.isLoading && !force) {
            return;
        }

        if (initializationRef.current.isInitialized && !force) {
            return;
        }

        // Cancel any existing request
        cleanup();

        // Create new abort controller
        initializationRef.current.abortController = new AbortController();
        const signal = initializationRef.current.abortController.signal;

        initializationRef.current.isLoading = true;
        setFilterState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            const { masterFiltersList, myLabFilterMaster, userDetail } = await getAllMasterFilterData({
                client: client,
                site: user.site,
                userID: user.id,
                signal, // Pass abort signal if the function supports it
                cachedUser: user
            });

            if (signal.aborted) {
                return;
            }

            setFilterState(prev => ({
                ...prev,
                masterDataFilters: masterFiltersList || {},
                myLabFilter: myLabFilterMaster,
                userFilters: userDetail?.userFilters || [],
                isLoading: false,
                lastUpdate: Date.now(),
                error: null,
            }));

            initializationRef.current.isInitialized = true;
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            console.error("SharedFilter: Error loading filter data:", error);
            setFilterState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Failed to load filter data'
            }));
        } finally {
            initializationRef.current.isLoading = false;
            initializationRef.current.abortController = null;
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, user?.site, user?.id, cleanup]);

    // Get current filter based on saved filter name
    const getCurrentFilter = useCallback((savedFilter) => {
        if (Object.keys(filterState.masterDataFilters).length === 0) {
            return null;
        }

        if (!savedFilter || savedFilter === "") {
            return null;
        } else if (savedFilter === ALL_EQUIPMENT) {
            return {
                filterName: ALL_EQUIPMENT,
                filterDefinition: {}
            };
        } else if (savedFilter === MY_LAB && filterState.myLabFilter) {
            const result = {
                filterName: MY_LAB,
                filterDefinition: filterState.myLabFilter?.filterDefinition || {}
            };
            return result;
        } else {
            const selectedFilter = filterState.userFilters.find(filter => filter.filterName === savedFilter);
            return selectedFilter || null;
        }
    }, [filterState.masterDataFilters, filterState.myLabFilter, filterState.userFilters]);

    // Memoized current filter calculation
    const memoizedCurrentFilter = useMemo(() => {
        return filterState.currentFilter;
    }, [filterState.currentFilter]);

    // Update current filter with debouncing to prevent rapid updates
    const updateCurrentFilter = useCallback((savedFilter) => {
        const newCurrentFilter = getCurrentFilter(savedFilter);

        setFilterState(prev => {
            const currentFilterString = JSON.stringify(prev.currentFilter);
            const newFilterString = JSON.stringify(newCurrentFilter);

            if (currentFilterString !== newFilterString) {
                return {
                    ...prev,
                    currentFilter: newCurrentFilter
                };
            } else {
                return prev;
            }
        });

        return newCurrentFilter;
    }, [getCurrentFilter]);

    // Check if filter data is ready
    const isFilterDataReady = useMemo(() => {
        return Object.keys(filterState.masterDataFilters).length > 0 &&
            !filterState.isLoading &&
            !filterState.error;
    }, [filterState.masterDataFilters, filterState.isLoading, filterState.error]);

    // Force refresh filter data
    const refreshFilterData = useCallback(() => {
        initializationRef.current.isInitialized = false;
        loadFilterData(true);
    }, [loadFilterData]);

    // Get filter statistics
    const getFilterStats = useCallback(() => {
        return {
            masterFiltersCount: Object.keys(filterState.masterDataFilters).length,
            userFiltersCount: filterState.userFilters.length,
            hasMyLabFilter: !!filterState.myLabFilter,
            isReady: isFilterDataReady,
            isLoading: filterState.isLoading,
            lastUpdate: filterState.lastUpdate,
            isInitialized: initializationRef.current.isInitialized,
            error: filterState.error,
        };
    }, [filterState, isFilterDataReady]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    const contextValue = useMemo(() => ({
        // State
        filterState,
        isFilterDataReady,
        isInitialized: initializationRef.current.isInitialized,

        // Actions
        loadFilterData,
        getCurrentFilter,
        updateCurrentFilter,
        refreshFilterData,
        getFilterStats,
        cleanup,

        // Computed values
        masterDataFilters: filterState.masterDataFilters,
        myLabFilter: filterState.myLabFilter,
        userFilters: filterState.userFilters,
        currentFilter: memoizedCurrentFilter,
        isLoading: filterState.isLoading,
        error: filterState.error,
    }), [
        filterState,
        isFilterDataReady,
        loadFilterData,
        getCurrentFilter,
        updateCurrentFilter,
        refreshFilterData,
        getFilterStats,
        cleanup,
        memoizedCurrentFilter,
    ]);

    return (
        <SharedFilterContext.Provider value={contextValue}>
            {children}
        </SharedFilterContext.Provider>
    );
};

export default SharedFilterContext;
